import type { Locale } from "../i18n/locale";
import { numberLocaleFor } from "../i18n/locale";
import type { UiKey } from "../i18n/ui";
import { estimateFromPersistedSnapshot } from "./estimateFromPersistedSnapshot";
import { MODEL_VERSION, SCHEMA_VERSION } from "./modelMeta";
import type { NamedScenarioEntry } from "./namedScenarios";
import type { PersistedUi } from "./persistedUi";

export type PackRow = { name: string; snapshot: PersistedUi };

function money(locale: Locale, usd: number): string {
  return usd.toLocaleString(numberLocaleFor(locale), {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function buildScenariosPackMarkdown(
  locale: Locale,
  t: (k: UiKey) => string,
  rows: PackRow[],
): string {
  const lines: string[] = [
    `# ${t("namedScenarios.title")} — pack`,
    "",
    `- **${t("export.appendix.modelVersion")}** \`${MODEL_VERSION}\` · **${t("export.appendix.schemaVersion")}** \`${SCHEMA_VERSION}\``,
    "",
    "| " +
      t("namedScenarios.packColScenario") +
      " | " +
      t("namedScenarios.packColTotal") +
      " |",
    "| --- | --- |",
  ];
  for (const r of rows) {
    const est = estimateFromPersistedSnapshot(r.snapshot, locale);
    const cell = est ? money(locale, est.totalMonthlyUsd) : "—";
    lines.push(`| ${r.name.replace(/\|/g, "\\|")} | ${cell} |`);
  }
  lines.push("", `*${t("export.appendix.disclaimer")}*`);
  return lines.join("\n");
}

export function buildScenariosPackJson(locale: Locale, rows: PackRow[]): string {
  const payload = {
    app: "dmeterline",
    modelVersion: MODEL_VERSION,
    schemaVersion: SCHEMA_VERSION,
    locale,
    generatedAt: new Date().toISOString(),
    scenarios: rows.map((r) => {
      const est = estimateFromPersistedSnapshot(r.snapshot, locale);
      return {
        name: r.name,
        totalMonthlyUsd: est?.totalMonthlyUsd ?? null,
        snapshot: r.snapshot,
      };
    }),
  };
  return JSON.stringify(payload, null, 2);
}

export function packRowsFromNamedAndCurrent(
  entries: NamedScenarioEntry[],
  currentLabel: string,
  currentSnapshot: PersistedUi,
): PackRow[] {
  const fromNamed = entries.map((e) => ({ name: e.name, snapshot: e.snapshot }));
  return [...fromNamed, { name: currentLabel, snapshot: currentSnapshot }];
}
